import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuthUserContext } from '../../providers/AuthUser';
import { DonorRecord, ApiDonorRecord } from '../../models/DonorRecord';
import { DonorReview, ApiDonorReview } from '../../models/DonorReview';
import { DonorReviewSummary, ApiDonorReviewSummary } from '../../models/DonorReviewSummary';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';

type DonorRecordWithReview = {
  donorRecord: DonorRecord;
  review?: DonorReview;
};

const DonorReviews = () => {
  const [ donorRecords, setDonorRecords ] = useState<DonorRecord[]>();
  const [ reviewSummary, setReviewSummary ] = useState<DonorReviewSummary>();
  const [ reviews, setReviews ] = useState<DonorReview[]>();
  const { get, post } = useApi();
  const { user } = useAuthUserContext();

  useEffect(() => {
    get({
      endpoint: `/api/v1/donor_records/index_for_reviews`,
      onSuccess: (res) => {
        const apiDonorRecords: ApiDonorRecord[] = res.data;
        setDonorRecords(apiDonorRecords.map(r => new DonorRecord(r)));
      },
      onError: (err) => {
        console.error("Error fetching donor records:", err);
        toast.error("提供実績の取得に失敗しました");
      },
    });

    get({
      endpoint: `/api/v1/donor_reviews`,
      onSuccess: (res) => {
        const apiReviews: ApiDonorReview[] = res.data.reviews || [];
        setReviews(apiReviews.map(r => new DonorReview(r)));

        const apiReviewSummary: ApiDonorReviewSummary = res.data.summary;
        setReviewSummary(new DonorReviewSummary(apiReviewSummary));
      },
      onError: (err) => {
        console.error("Error fetching donor reviews:", err);
        toast.error("レビューの取得に失敗しました");
      },
    });
  }, [ get ]);

  const handleToggleReviewVisibility = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isHidden = !e.target.checked; // checked=true → is_hidden=false

    if (!user?.userId) return;

    post({
      endpoint: `/api/v1/donors/${ user.userId }/review_settings`,
      method: 'PATCH',
      body: { is_hidden: isHidden },
      onSuccess: () => {
        // reviewSummaryのis_hiddenを更新
        setReviewSummary(prev => {
          if (!prev) return prev;
          return new DonorReviewSummary({
            id: prev.id,
            donor_id: prev.donorId,
            review_count: prev.reviewCount,
            total_score: prev.totalScore,
            average_score: prev.averageScore,
            is_hidden: isHidden,
          });
        });
        toast.success(isHidden ? 'レビューをプロフィールから非表示にしました' : 'レビューをプロフィールに表示しました');
      },
      onError: (err) => {
        console.error("Error updating review settings:", err);
        toast.error("設定の更新に失敗しました");
      },
    });
  };

  // Merge reviews to donor records
  const donorRecordWithReviews: DonorRecordWithReview[] = (donorRecords || []).map(donorRecord => {
    const review = (reviews || []).find(r => r.donee.userNo === donorRecord.donee.userNo);
    return {
      donorRecord,
      review,
    };
  });

  const reviewRows = donorRecordWithReviews.map(drwr => {
    const donee = drwr.donorRecord.donee;
    const review = drwr.review;

    return (
      <tr key={ drwr.donorRecord.id }>
        <td>{ donee.name } (No.{ donee.userNo })</td>
        <td>
          { review ? (
            <span className="stars">{ review.stars }</span>
          ) : (
            <span>-</span>
          )}
        </td>
        <td className="comment">{ review ? review.comment : '-' }</td>
        <td>{ review ? review.ageRangeText : '-' }</td>
      </tr>
    );
  });

  return (
    <section className="donor_reviews">
      <h2 className="main_title">
        <span>レビュー</span>
      </h2>
      <dl className="graf_list form">
        <dt>表示設定</dt>
        <dd>
          <span className="wpcf7-form-control-wrap checkbox-content">
            <span className="wpcf7-form-control wpcf7-checkbox">
              <span className="wpcf7-list-item">
                <label>
                  <input
                    type="checkbox"
                    checked={ !(reviewSummary?.isHidden ?? false) }
                    onChange={ handleToggleReviewVisibility }
                  />
                  <span className="wpcf7-list-item-label">プロフィールに表示する</span>
                </label>
              </span>
            </span>
          </span>
        </dd>
        <dt>平均</dt>
        <dd><span className="stars">{ reviewSummary ? reviewSummary.stars : '-' }</span> { reviewSummary?.averageScore || "0.0" }</dd>
        <dt>レビュー数</dt>
        <dd>{ reviewSummary?.reviewCount || "0" } 件</dd>
      </dl>
      <div className="tableform">
        <div className="scroll">
          <table>
            <tbody>
              <tr>
                <th>ドニー</th>
                <th>評価</th>
                <th>コメント</th>
                <th>年齢</th>
              </tr>
              { !donorRecords || !reviews ? (
                <tr>
                  <td colSpan={ 6 }>
                    <Spinner centered={ true } color="blue" />
                  </td>
                </tr>
              ) : donorRecordWithReviews.length > 0 ? (
                reviewRows
              ) : (
                <tr>
                  <td colSpan={ 6 }>提供実績を承認済みのドニーがいません</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p>
          ※ 提供実績を承認したドニーのみがレビューを記入できます。<br/>
          ※ 表示設定をOFFにすると、レビューが「0件」としてプロフィールに表示されます。<br/>
          ※ レビュー内容の修正が必要な場合はドニー様へ直接ご依頼ください。<br/>
        </p>
      </div>
    </section>
  );
};

export default DonorReviews;
